import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Chip,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
} from '@mui/icons-material';
import { CreateTaskDTO, TaskStatus, todoService } from '../../services/api/todo.service';
import WorkflowService, { Workflow } from '../../services/api/workflow.service';
import { AgentService } from '../../services/api';

interface UserAgent {
  id: string;
  name: string;
  type: string;
  is_custom?: boolean;
  custom_name?: string;
}

interface EnhancedTaskCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (taskData: CreateTaskDTO) => void;
  columnId?: string;
}

const EnhancedTaskCreateDialog: React.FC<EnhancedTaskCreateDialogProps> = ({
  open,
  onClose,
  onSubmit,
  columnId
}) => {
  const [taskData, setTaskData] = useState<Partial<CreateTaskDTO>>({
    title: '',
    description: '',
    task_status_id: '',
    priority: 'medium',
    due_date: '',
    estimated_hours: 0,
    assignee_ids: [],
    label_ids: [],
    assigned_user_agent_id: '',
    workflow_id: ''
  });
  const [taskStatuses, setTaskStatuses] = useState<TaskStatus[]>([]);
  const [loadingStatuses, setLoadingStatuses] = useState(false);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [loadingWorkflows, setLoadingWorkflows] = useState(false);
  const [userAgents, setUserAgents] = useState<UserAgent[]>([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [assignmentType, setAssignmentType] = useState<'none' | 'ai_teammate' | 'workflow'>('none');

  const fetchTaskStatuses = useCallback(async () => {
    setLoadingStatuses(true);
    try {
      const statuses = await todoService.getTaskStatuses();
      setTaskStatuses(statuses.sort((a, b) => a.position - b.position));

      // Set default status if available
      const defaultStatus = statuses.find(s => s.is_default);
      if (defaultStatus) {
        setTaskData(prev => ({ ...prev, task_status_id: prev.task_status_id || defaultStatus.id }));
      }
    } catch (error) {
      console.error('Failed to fetch task statuses:', error);
    } finally {
      setLoadingStatuses(false);
    }
  }, []);

  const fetchWorkflows = useCallback(async () => {
    setLoadingWorkflows(true);
    try {
      const { workflows: workflowList } = await WorkflowService.getUserWorkflows();
      setWorkflows(workflowList.filter(w => w.isActive));
    } catch (error) {
      console.error('Failed to fetch workflows:', error);
    } finally {
      setLoadingWorkflows(false);
    }
  }, []);

  const fetchUserAgents = useCallback(async () => {
    setLoadingAgents(true);
    try {
      const agentService = new AgentService();
      const agents = await agentService.getAgents();

      // Transform agents to include both default and custom agents
      const formattedAgents = agents.map((agent: any) => ({
        id: agent.id,
        name: agent.custom_name || agent.name,
        type: agent.type,
        is_custom: agent.is_custom || false,
        custom_name: agent.custom_name
      }));

      setUserAgents(formattedAgents);
    } catch (error) {
      console.error('Failed to fetch user agents:', error);
    } finally {
      setLoadingAgents(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      // Fetch task statuses, workflows, and user agents when dialog opens
      fetchTaskStatuses();
      fetchWorkflows();
      fetchUserAgents();

      // Reset form when dialog opens
      setTaskData({
        title: '',
        description: '',
        task_status_id: '',
        priority: 'medium',
        due_date: '',
        estimated_hours: 0,
        assignee_ids: [],
        label_ids: [],
        assigned_user_agent_id: '',
        workflow_id: ''
      });
      setAssignmentType('none');
    }
  }, [open, columnId, fetchTaskStatuses, fetchWorkflows, fetchUserAgents]);



  const handleInputChange = (field: keyof CreateTaskDTO, value: any) => {
    setTaskData(prev => ({ ...prev, [field]: value }));
  };

  const handleAssignmentTypeChange = (type: 'none' | 'ai_teammate' | 'workflow') => {
    setAssignmentType(type);

    // Clear the other assignment fields when switching types
    setTaskData(prev => ({
      ...prev,
      assigned_user_agent_id: type === 'ai_teammate' ? prev.assigned_user_agent_id : '',
      workflow_id: type === 'workflow' ? prev.workflow_id : ''
    }));
  };


  const handleSubmit = () => {
    // Validate required fields
    if (!taskData.title?.trim()) {
      return;
    }

    // Prepare final task data
    const finalTaskData: CreateTaskDTO = {
      title: taskData.title.trim(),
      description: taskData.description || undefined,
      task_status_id: taskData.task_status_id || undefined,
      priority: taskData.priority || 'medium',
      due_date: taskData.due_date || undefined,
      estimated_hours: taskData.estimated_hours || undefined,
      assignee_ids: taskData.assignee_ids || [],
      label_ids: taskData.label_ids || [],
      assigned_ai_teammate: assignmentType === 'ai_teammate' ? taskData.assigned_ai_teammate || undefined : undefined,
      workflow_id: assignmentType === 'workflow' ? taskData.workflow_id || undefined : undefined
    };

    // Submit task data
    onSubmit(finalTaskData);
    onClose();
  };

  const formatDueDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().slice(0, 16);
  };

  const handleDueDateChange = (dateString: string) => {
    if (dateString) {
      handleInputChange('due_date', new Date(dateString).toISOString());
    } else {
      handleInputChange('due_date', '');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: { minHeight: '500px' }
      }}
    >
      <DialogTitle style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Typography variant="h6">Create New Task</Typography>
        </div>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent style={{ paddingBottom: 8 }}>
        <div style={{ marginBottom: 24 }}>
          <TextField
            fullWidth
            label="Task Title"
            value={taskData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            placeholder="What needs to be done?"
            variant="outlined"
            autoFocus
            required
            style={{ marginBottom: 16 }}
          />

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Description"
            value={taskData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Provide additional details about this task..."
            variant="outlined"
            style={{ marginBottom: 16 }}
          />


        </div>

        {/* Assignment Section */}
        <div style={{ marginBottom: 24 }}>
          <Typography variant="subtitle2" gutterBottom>
            Task Assignment
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Assignment Type</InputLabel>
                <Select
                  value={assignmentType}
                  label="Assignment Type"
                  onChange={(e) => handleAssignmentTypeChange(e.target.value as 'none' | 'ai_teammate' | 'workflow')}
                >
                  <MenuItem value="none">No Assignment</MenuItem>
                  <MenuItem value="ai_teammate">AI Teammate</MenuItem>
                  <MenuItem value="workflow">Workflow</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {assignmentType === 'ai_teammate' && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>AI Teammate</InputLabel>
                  <Select
                    value={taskData.assigned_user_agent_id || ''}
                    label="AI Teammate"
                    onChange={(e) => handleInputChange('assigned_user_agent_id', e.target.value)}
                    disabled={loadingAgents}
                  >
                    <MenuItem value="">Select AI Teammate</MenuItem>
                    {userAgents.map(agent => (
                      <MenuItem key={agent.id} value={agent.id}>
                        {agent.name}
                        {agent.is_custom && (
                          <Chip
                            label="Custom"
                            size="small"
                            style={{ marginLeft: 8 }}
                            variant="outlined"
                          />
                        )}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {assignmentType === 'workflow' && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Workflow</InputLabel>
                  <Select
                    value={taskData.workflow_id || ''}
                    label="Workflow"
                    onChange={(e) => handleInputChange('workflow_id', e.target.value)}
                    disabled={loadingWorkflows}
                  >
                    {loadingWorkflows ? (
                      <MenuItem disabled>Loading workflows...</MenuItem>
                    ) : workflows.length === 0 ? (
                      <MenuItem disabled>No workflows available</MenuItem>
                    ) : (
                      [
                        <MenuItem key="select-workflow" value="">Select Workflow</MenuItem>,
                        ...workflows.map((workflow) => (
                          <MenuItem key={workflow.id} value={workflow.id}>
                            {workflow.name}
                            {workflow.description && (
                              <Typography variant="caption" style={{ marginLeft: 8, color: 'rgba(0,0,0,0.6)' }}>
                                - {workflow.description.substring(0, 50)}
                                {workflow.description.length > 50 ? '...' : ''}
                              </Typography>
                            )}
                          </MenuItem>
                        ))
                      ]
                    )}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </div>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={taskData.task_status_id || ''}
                label="Status"
                onChange={(e) => handleInputChange('task_status_id', e.target.value)}
                disabled={loadingStatuses}
              >
                {loadingStatuses ? (
                  <MenuItem disabled>Loading statuses...</MenuItem>
                ) : (
                  taskStatuses.map((status) => (
                    <MenuItem key={status.id} value={status.id}>
                      {status.label}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={taskData.priority || 'medium'}
                label="Priority"
                onChange={(e) => handleInputChange('priority', e.target.value)}
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="urgent">Urgent</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="datetime-local"
              label="Due Date"
              value={formatDueDate(taskData.due_date || '')}
              onChange={(e) => handleDueDateChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Estimated Hours"
              value={taskData.estimated_hours || ''}
              onChange={(e) => handleInputChange('estimated_hours', Number(e.target.value) || 0)}
              inputProps={{ min: 0, step: 0.5 }}
            />
          </Grid>
        </Grid>

      </DialogContent>

      <DialogActions style={{ paddingLeft: 24, paddingRight: 24, paddingBottom: 16 }}>
        <Button onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!taskData.title?.trim()}
        >
          Create Task
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EnhancedTaskCreateDialog;